import { gql } from '@apollo/client'
import * as React from 'react'
import { appContext } from '../../../common/src/context'
import {
  FetchAgentVirtualTourQuery,
  FetchAgentVirtualTourQueryVariables,
  FetchProspectVirtualTourQuery,
  FetchProspectVirtualTourQueryVariables,
} from '../../../common/src/types.gen'
import { createRequestScopedClient } from '../apolloClientForLambda'
import VirtualTour, { VirtualTourProps } from '../virtual-tour/VirtualTour'
import { createWidget } from './base'

const virtualTourWidgetId = 'virtual-tour'
const virtualTourWidget = createWidget(virtualTourWidgetId, (props: VirtualTourProps) => <VirtualTour {...props} />)

const defaultErrorMessage = 'Bad Request'

const fetchAgentVirtualTour = gql`
  query FetchAgentVirtualTour($agentId: String!, $firmId: String!, $hash: String!) {
    agentVirtualTour(agentId: $agentId, firmId: $firmId, hash: $hash) {
      contact {
        id
        firstName
      }
      calendarLink
      showing {
        id
        leasingTask {
          id
          displayName
        }
        unitType
        showingContactsPlanningToAttend {
          contact {
            id
            firstName
            lastName
          }
          isAgent
        }
        calEvent {
          id
          timeStart
          timeEnd
        }
      }
      dailyCallOptions {
        url
        token
      }
    }
  }
`

const fetchProspectVirtualTour = gql`
  query FetchProspectVirtualTour($showingContactId: String!, $hash: String!) {
    prospectVirtualTour(showingContactId: $showingContactId, hash: $hash) {
      dailyCallOptions {
        url
        token
      }
    }
  }
`

export async function getVirtualTourHTML(params: { [key: string]: string } | null) {
  const props = await createVirtualTourProps(params)
  return virtualTourWidget.renderHtml('Virtual Tour', props, props)
}

async function createVirtualTourProps(params: { [key: string]: string } | null): Promise<VirtualTourProps> {
  if (params == null) {
    return {
      error: defaultErrorMessage,
    }
  }

  const { agent, firm, hash, sc, h } = params

  const client = createRequestScopedClient()

  if (agent != null && firm != null && hash != null) {
    try {
      const { data, error } = await client.query<FetchAgentVirtualTourQuery, FetchAgentVirtualTourQueryVariables>({
        query: fetchAgentVirtualTour,
        variables: {
          agentId: agent,
          firmId: firm,
          hash,
        },
      })

      if (error) {
        return {
          error: error.message,
        }
      }

      return {
        virtualTour: data.agentVirtualTour,
      }
    } catch (e) {
      return {
        error: defaultErrorMessage,
      }
    }
  }

  if (sc != null && h != null) {
    try {
      const { data, error } = await client.query<FetchProspectVirtualTourQuery, FetchProspectVirtualTourQueryVariables>(
        {
          query: fetchProspectVirtualTour,
          variables: {
            showingContactId: sc,
            hash: h,
          },
        }
      )

      if (error) {
        return {
          error: error.message,
        }
      }

      return {
        virtualTour: data.prospectVirtualTour,
      }
    } catch (e) {
      return {
        error: defaultErrorMessage,
      }
    }
  }

  return {
    error: defaultErrorMessage,
  }
}

/**
 * Hydrates server-rendered widget. This is a no-op for non-daily virtual tours.
 */
export function hydrateVirtualTourWidget() {
  // Only hydrate widget if it's actually rendered
  if (document.getElementById(virtualTourWidgetId) == null) {
    return
  }

  virtualTourWidget.init(appContext().hydrateProps)
}
